// Minting Printess save tokens for preview renders (#5328).
//
// A save token is a design Printess stores for us, and it is the ONLY artefact that
// carries the student's photo: the render-only service API ignores injected images
// entirely (measured -- identical bytes before and after an injection). The token can
// only be produced by the buyer-side editor, so producing one means booting a hidden
// editor, importing the pose, assigning it to the template's photo frame and saving.
//
// That is slow (a few seconds per template) and happens ONCE per (template, pose);
// afterwards Shop::PrintessPreview turns the token into image URLs in milliseconds.
//
// ONE implementation, shared by the storefront gallery and the admin preview page --
// per CLAUDE.md's "one implementation per task". The sequence below is not obvious
// (see the comments on each step) and a second copy of it would drift.

const EDITOR_ORIGIN = 'https://editor.printess.com';
const EDITOR_URL = `${EDITOR_ORIGIN}/printess-editor/embed.html`;

// Frames a template marks with this class are the ones a student photo belongs in.
// A template with no such frame renders identically with and without an injection --
// which is how "this template cannot take a photo" shows up.
const PHOTO_FRAME_CLASS = 'photoframe';

// The editor applies an assignment asynchronously and says nothing when it lands;
// saving too early stores the design without the photo in it.
const ASSIGN_SETTLE_MS = 1500;

/**
 * Mint a save token per template, one at a time, in a hidden iframe.
 *
 * @param {Object}   opts
 * @param {HTMLIFrameElement} opts.frame     hidden iframe to drive
 * @param {string}   opts.token              Printess SHOP token (public, buyer-side)
 * @param {string}   opts.poseUrl            the WATERMARKED pose -- never a full-res one
 * @param {number|string} opts.poseId        identifies the design; part of the basket id
 * @param {string[]} opts.templates          template names still needing a token
 * @param {Function} opts.onMinted           async (template, saveToken) -> stored
 * @param {Function} [opts.onProgress]       (template|null, remaining) for status text
 * @returns {Function} call it to stop minting (component unmount, navigation away)
 */
export default function mintPreviewTokens({
  frame, token, poseUrl, poseId, templates, onMinted, onProgress,
}) {
  if (!frame || !token || !poseUrl || !templates || !templates.length) {
    if (onProgress) onProgress(null, 0);
    return () => {};
  }

  const pending = [...templates];
  let current = null;
  let stopped = false;

  const post = (message) => {
    if (!stopped && frame.contentWindow) frame.contentWindow.postMessage(message, '*');
  };

  const mintNext = () => {
    if (stopped) return;
    current = pending.shift();
    if (!current) {
      if (onProgress) onProgress(null, 0);
      return;
    }
    if (onProgress) onProgress(current, pending.length);
    frame.onload = () => post({
      cmd: 'attach',
      properties: {
        token,
        templateName: current,
        basketId: `preview_${current}_${poseId}`,
        shopUserId: 'shoob-preview',
      },
    });
    // A fresh query string each time: the editor caches its own boot otherwise and
    // the second template never loads.
    frame.src = `${EDITOR_URL}?ts=${Date.now()}`;
  };

  const onMessage = (event) => {
    if (stopped || event.origin !== EDITOR_ORIGIN) return;
    const data = event.data || {};

    // Template ready: put the student's pose in it. `false`, never `true` --
    // auto-assign drops the photo into a NEW full-size frame instead of the
    // template's sized one, which renders the photo over the whole product.
    if (data.cmd === 'loaded') {
      post({ cmd: 'importImageFromUrl', parameters: [poseUrl, false] });
      return;
    }

    if (data.cmd === 'importImageFromUrl' && data.result) {
      const imageId = data.result.id || data.result;
      // NOTE the spelling: `assingImageByIdToFramesWithClass` is misspelled in
      // Printess's own API and the correct spelling is not a method.
      post({
        cmd: 'assingImageByIdToFramesWithClass',
        parameters: [imageId, PHOTO_FRAME_CLASS, 'document'],
      });
      setTimeout(() => post({ cmd: 'saveAndGenerateBasketThumbnailUrl', parameters: [600, 600] }),
        ASSIGN_SETTLE_MS);
      return;
    }

    if (data.cmd === 'saveAndGenerateBasketThumbnailUrl' && data.result && data.result.saveToken) {
      Promise.resolve(onMinted(current, data.result.saveToken)).then(mintNext, mintNext);
    }
  };

  window.addEventListener('message', onMessage);
  mintNext();

  return () => {
    stopped = true;
    window.removeEventListener('message', onMessage);
  };
}
